import { useEffect, useMemo, useState } from "react";
import {
  getMyPendings,
  getPendingCounts,
  ListParams,
  PendienteRow,
  generateFormsForMissing,
  submitAlumnoForm,
} from "../../api/firmas";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

type SortKey = NonNullable<ListParams["sort"]>;

export default function MisFirmasPendientes() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation<"translation">();

  const [rows, setRows] = useState<PendienteRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("fecha_desc");
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(20);

  const [counts, setCounts] = useState<{
    total: number;
    profesor: number;
    alumnos: number;
    soloProfesor: number;
    soloAlumnos: number;
    // dejamos 'ambos' en el tipo si te lo devuelve el backend, pero no lo usamos
    ambos?: number;
  }>({
    total: 0,
    profesor: 0,
    alumnos: 0,
    soloProfesor: 0,
    soloAlumnos: 0,
  });

  // loading por reserva al enviar formularios
  const [sendingForm, setSendingForm] = useState<Record<number, boolean>>({});

  async function load() {
    setLoading(true);
    try {
      const data = await getMyPendings({ search, sort, skip, take });
      setRows(data.rows);
      setTotal(data.total);
      const c = await getPendingCounts();
      setCounts(c);
    } catch (e) {
      setRows([]);
      setTotal(0);
      enqueueSnackbar(t("pendingSignatures.toast.errorLoad"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [search, sort, skip, take]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("firmas:signed", h);
    return () => window.removeEventListener("firmas:signed", h);
  }, []);

  // Agrupación visual
  const grupos = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const ayer = new Date(hoy);
    ayer.setDate(hoy.getDate() - 1);
    const hace7 = new Date(hoy);
    hace7.setDate(hoy.getDate() - 7);

    const H: PendienteRow[] = [];
    const A: PendienteRow[] = [];
    const L7: PendienteRow[] = [];
    const MA: PendienteRow[] = [];

    for (const r of rows) {
      const d = new Date(r.fecha + "T00:00:00");
      if (d.getTime() === hoy.getTime()) H.push(r);
      else if (d.getTime() === ayer.getTime()) A.push(r);
      else if (d >= hace7) L7.push(r);
      else MA.push(r);
    }
    return { H, A, L7, MA };
  }, [rows]);

  function openFirmaDialog(reservaId: number) {
    window.dispatchEvent(new CustomEvent("dialog:firma:open", { detail: { reservaId } }));
  }

  async function handleFormularios(reservaId: number) {
    try {
      setSendingForm((m) => ({ ...m, [reservaId]: true }));
      const links = await generateFormsForMissing(reservaId);
      if (!Array.isArray(links) || links.length === 0) {
        enqueueSnackbar(t("pendingSignatures.forms.infoNoMissingOrTooEarly"), {
          variant: "info",
        });
        setSendingForm((m) => ({ ...m, [reservaId]: false }));
        return;
      }

      let enviados = 0;
      for (const it of links) {
        if (String(it.rol) !== "alumno") continue;
        const url = String(it.url || "");
        const token = (url.match(/[?&]token=([^&]+)/) || [])[1];
        if (!token) continue;

        // Obtenemos el nombre del alumno a partir del token del formulario
        let alumnoNombre = `#${it.idUsuario}`;
        try {
          const { data } = await api.get("/form-firma", { params: { token } });
          if (data?.alumno) alumnoNombre = data.alumno;
        } catch {
          /* si falla, usamos el fallback */
        }

        const motivo = window
          .prompt(
            String(
              t("pendingSignatures.forms.promptReasonByName", {
                name: alumnoNombre,
              })
            ),
            ""
          )
          ?.trim();
        if (!motivo) continue;

        await submitAlumnoForm(token, motivo);
        enviados++;
      }

      if (enviados > 0) {
        enqueueSnackbar(
          enviados === 1
            ? t("pendingSignatures.forms.sentOne")
            : t("pendingSignatures.forms.sentMany", { n: enviados }),
          { variant: "success" }
        );
        window.dispatchEvent(new CustomEvent("firmas:signed"));
        await load();
      } else {
        enqueueSnackbar(t("pendingSignatures.forms.noneSent"), {
          variant: "info",
        });
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ?? e?.message ?? t("pendingSignatures.forms.errorSubmit");
      enqueueSnackbar(String(msg), { variant: "error" });
    } finally {
      setSendingForm((m) => ({ ...m, [reservaId]: false }));
    }
  }

  const pageCount = Math.ceil(total / take);

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {t("pendingSignatures.title")}
        <small style={{ fontSize: 14, fontWeight: 400, color: "#555" }}>
          {t("pendingSignatures.header.counts", {
            total: counts.total ?? 0,
            profesor: counts.profesor ?? 0,
            alumnos: counts.alumnos ?? 0,
          })}
        </small>
      </h1>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          placeholder={String(t("pendingSignatures.searchPlaceholder"))}
          value={search}
          onChange={(e) => {
            setSkip(0);
            setSearch(e.target.value);
          }}
          style={{
            flex: 1,
            padding: "8px 12px",
            fontSize: "0.85rem",
            borderRadius: "4px",
            border: "1px solid #ccc", // borde fino gris inicial
            outline: "none",
            boxSizing: "border-box", // importante para que no salte
            height: 36, // altura fija
            backgroundColor: "#fff",
            transition: "all 0.2s",
          }}
          onFocus={(e) => {
            e.currentTarget.style.border = "2px solid #21a598"; // borde más grueso y verde
          }}
          onBlur={(e) => {
            e.currentTarget.style.border = "1px solid #ccc"; // volver a borde fino gris
          }}
        />

        <select
          value={sort}
          onChange={(e) => {
            setSkip(0);
            setSort(e.target.value as SortKey);
          }}
          style={{
            minWidth: 180, // ancho mínimo para que quepan palabras largas
            padding: "8px 12px",
            fontSize: "0.85rem",
            borderRadius: "4px",
            border: "1px solid #ccc", // borde fino gris inicial
            outline: "none",
            boxSizing: "border-box",
            height: 36, // altura fija igual que el input
            backgroundColor: "#fff",
            transition: "all 0.2s",
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            cursor: "pointer",
          }}
          onFocus={(e) => {
            e.currentTarget.style.border = "2px solid #21a598"; // borde verde al focus
          }}
          onBlur={(e) => {
            e.currentTarget.style.border = "1px solid #ccc"; // volver a borde gris
          }}
        >
          <option value="fecha_desc">{t("pendingSignatures.sort.dateDesc")}</option>
          <option value="fecha_asc">{t("pendingSignatures.sort.dateAsc")}</option>
          <option value="asignatura">{t("pendingSignatures.sort.subject")}</option>
          <option value="alumno">{t("pendingSignatures.sort.student")}</option>
          <option value="centro">{t("pendingSignatures.sort.center")}</option>
        </select>
      </div>

      {loading ? (
        <p>{t("pendingSignatures.loading")}</p>
      ) : rows.length === 0 ? (
        <p>{t("pendingSignatures.empty")}</p>
      ) : (
        <>
          {renderGrupo(
            t("pendingSignatures.group.today"),
            grupos.H,
            openFirmaDialog,
            handleFormularios,
            sendingForm,
            t
          )}
          {renderGrupo(
            t("pendingSignatures.group.yesterday"),
            grupos.A,
            openFirmaDialog,
            handleFormularios,
            sendingForm,
            t
          )}
          {renderGrupo(
            t("pendingSignatures.group.last7"),
            grupos.L7,
            openFirmaDialog,
            handleFormularios,
            sendingForm,
            t
          )}
          {renderGrupo(
            t("pendingSignatures.group.older"),
            grupos.MA,
            openFirmaDialog,
            handleFormularios,
            sendingForm,
            t
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                color: "#21a598",
                borderColor: "#21a598",
                textTransform: "uppercase",
                fontWeight: 400,
                boxShadow: "none",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(33,165,152,0.08)",
                  borderColor: "#1b887f",
                  boxShadow: "none",
                },
              }}
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - take))}
            >
              {t("pendingSignatures.pager.prev")}
            </Button>

            <span>
              {t("pendingSignatures.pager.page", {
                page: Math.floor(skip / take) + 1,
                pages: Math.max(1, pageCount),
              })}
            </span>

            <Button
              variant="outlined"
              sx={{
                color: "#21a598",
                borderColor: "#21a598",
                textTransform: "uppercase",
                fontWeight: 400,
                boxShadow: "none",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(33,165,152,0.08)",
                  borderColor: "#1b887f",
                  boxShadow: "none",
                },
              }}
              disabled={skip + take >= total}
              onClick={() => setSkip(skip + take)}
            >
              {t("pendingSignatures.pager.next")}
            </Button>

            <FormControl
              size="small"
              sx={{
                width: 62,
                height: 36,
                "& .MuiOutlinedInput-root": {
                  height: "100%",
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#21a598 !important",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#21a598 !important",
                  },
                },
              }}
            >
              <Select
                value={take}
                onChange={(e) => {
                  setSkip(0);
                  setTake(Number(e.target.value));
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        backgroundColor: "#fff",
                        color: "#000",
                        textAlign: "center",
                        "&:hover": {
                          backgroundColor: "#fcf1ad !important",
                          color: "#000 !important",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#fff !important",
                          color: "#000 !important",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#fcf1ad !important",
                          color: "#000 !important",
                        },
                      },
                    },
                  },
                }}
                sx={{
                  textAlign: "center",
                  padding: "2px -5px",
                  fontSize: "0.85rem",
                  height: "100%",
                  backgroundColor: "#fff",
                  color: "#000",
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </div>
        </>
      )}
    </div>
  );
}

function renderGrupo(
  titulo: string,
  rows: PendienteRow[],
  onFirmar: (id: number) => void,
  onFormAlumnos: (id: number) => void,
  sendingForm: Record<number, boolean>,
  t: (key: string, opts?: any) => string
) {
  if (!rows.length) return null;
  return (
    <section style={{ marginTop: 16 }}>
      <h3>{titulo}</h3>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          borderRadius: 6,
          overflow: "hidden",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#000" }}>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "left",
              }}
            >
              {t("pendingSignatures.sort.dateAsc")}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "left",
              }}
            >
              {t("pendingSignatures.sort.student")}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "left",
              }}
            >
              {t("pendingSignatures.sort.subject")}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "left",
              }}
            >
              {t("pendingSignatures.sort.student")}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "left",
              }}
            >
              {t("pendingSignatures.sort.center")}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              {" "}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              {" "}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              {" "}
            </th>
            <th
              style={{
                padding: "8px 12px",
                color: "#fff",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              {" "}
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => {
            const faltaAlumnos = Number(r.alumFirmados || 0) < Number(r.alumTotal || 0);
            const profSignedBool = r.profSigned === true || Number(r.profSigned) === 1;
            const faltaProfe = !profSignedBool;

            const rowBg = i % 2 === 0 ? "#fff" : "#f9f9f9";

            return (
              <tr key={r.reservaId} style={{ backgroundColor: rowBg, fontSize: "0.85rem" }}>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {r.fecha}
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {r.horaInicio}–{r.horaFin}
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {r.asignatura}
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {r.alumnos}
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {r.centro}
                </td>

                {/* Botón Firmar */}
                <td style={{ padding: "6px 0px", borderBottom: "1px solid #eee" }}>
                  <Button
                    variant="outlined"
                    sx={{
                      color: "#21a598",
                      borderColor: "#21a598",
                      textTransform: "uppercase",
                      fontWeight: 400,
                      borderRadius: "4px",
                      padding: "2px 12px",
                      fontSize: "0.85rem",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "rgba(33,165,152,0.08)",
                        borderColor: "#1b887f",
                        boxShadow: "none",
                      },
                    }}
                    onClick={() => onFirmar(r.reservaId)}
                  >
                    {t("pendingSignatures.btn.sign")}
                  </Button>
                </td>

                {/* Teacher Chip */}
                <td
                  style={{
                    padding: "0px 0px",
                    borderBottom: "1px solid #eee",
                    textAlign: "center",
                  }}
                >
                  {faltaProfe && (
                    <span
                      style={{
                        padding: "2px 6px",
                        borderRadius: 6,
                        background: "#fcf1ad",
                        color: "rgb(84, 84, 72)",
                        fontSize: 12,
                      }}
                      title={t("pendingSignatures.chips.teacherMissing")}
                    >
                      {t("pendingSignatures.chips.teacher")}
                    </span>
                  )}
                </td>

                {/* Student Form Button ahora en penúltima columna */}
                <td
                  style={{
                    padding: "0px 0px",
                    borderBottom: "1px solid #eee",
                    textAlign: "center",
                  }}
                >
                  {faltaAlumnos && (
                    <Button
                      variant="outlined"
                      sx={{
                        color: "#21a598",
                        borderColor: "#21a598",
                        textTransform: "uppercase",
                        fontWeight: 400,
                        borderRadius: "4px",
                        padding: "2px 12px",
                        fontSize: "0.85rem",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "rgba(33,165,152,0.08)",
                          borderColor: "#1b887f",
                          boxShadow: "none",
                        },
                      }}
                      disabled={!!sendingForm[r.reservaId]}
                      onClick={() => onFormAlumnos(r.reservaId)}
                      title={t("pendingSignatures.forms.tooltip")}
                    >
                      {sendingForm[r.reservaId]
                        ? t("pendingSignatures.forms.sending")
                        : t("pendingSignatures.btn.forms")}
                    </Button>
                  )}
                </td>

                {/* Student Chip ahora en última columna */}
                <td
                  style={{
                    padding: "0px 0px",
                    borderBottom: "1px solid #eee",
                    textAlign: "center",
                  }}
                >
                  {faltaAlumnos && (
                    <span
                      style={{
                        padding: "2px 6px",
                        borderRadius: 6,
                        background: "#fcf1ad",
                        color: "rgb(84, 84, 72)",
                        fontSize: 12,
                      }}
                      title={t("pendingSignatures.chips.studentsMissing")}
                    >
                      {t("pendingSignatures.chips.students", {
                        signed: Number(r.alumFirmados) || 0,
                        total: Number(r.alumTotal) || 0,
                      })}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  padding: "8px",
};
const td: React.CSSProperties = {
  borderBottom: "1px solid #eee",
  padding: "8px",
};
